import React from 'react';
import Footer from '@klara/login-ui/Footer.jsx';
import { authApi } from '@/lib/api/auth';

interface TenantCredentials {
  tenantCode: string;
  email: string;
  password: string;
}

interface LoginPageProps {
  onAuth: (credentials: TenantCredentials) => Promise<void>;
  initialError?: string;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as {
      message?: string;
      response?: { data?: { detail?: string; message?: string } };
    };
    return candidate.response?.data?.detail || candidate.response?.data?.message || candidate.message || 'Sign in failed.';
  }
  return 'Sign in failed.';
}

function getAdminConsoleUrl(): string {
  const origin = window.location.origin;
  if (origin.includes('-5173.')) {
    return origin.replace('-5173.', '-4180.');
  }
  if (origin.includes(':5173')) {
    return origin.replace(':5173', ':4180');
  }
  return 'http://localhost:4180';
}

const RESERVED_SUBDOMAINS = new Set([
  'www',
  'app',
  'admin',
  'api',
  'auth',
  'portal',
  'klara',
  'dev',
  'staging',
  'prod',
  'test',
  'localhost',
  'asse',
  'inc1',
  'use',
  'usw',
  'euw',
  'eun',
  'devtunnels',
]);

const TUNNEL_DOMAINS = [
  '.devtunnels.ms',
  '.ngrok.io',
  '.ngrok-free.app',
  '.loca.lt',
  '.trycloudflare.com',
  '.cloudflarepreview.com',
];

// Suffixes appended to a client's base subdomain by the per-client
// Kubernetes deployment (deploy/helm/klara-client) — checked longest-first
// so a client name that itself ends in one of these isn't double-stripped.
const CLIENT_HOST_SUFFIXES = ['-admin', '-app'];

function isValidTenantCode(code: string | null | undefined): boolean {
  if (!code || typeof code !== 'string') return false;
  const clean = code.trim().toLowerCase();
  if (!clean || RESERVED_SUBDOMAINS.has(clean)) return false;
  // Exclude port suffixes and domain artifacts. Deliberately does NOT
  // exclude by charset/length alone (an exactly-8-lowercase-char check used
  // to live here) — that silently rejected any real tenant code that
  // happened to be 8 characters (e.g. "kanerika"). The random-tunnel-ID
  // heuristic that actually needs is scoped to isLikelyRandomTunnelId
  // below, applied only to dev-tunnel-derived candidates.
  if (clean.includes('.devtunnels') || /-[0-9]{4,5}$/.test(clean)) {
    return false;
  }
  return /^[a-z0-9-_]{2,64}$/.test(clean);
}

// Azure Dev Tunnels without a custom name get an 8-char random hex-ish
// subdomain (e.g. "25830gmh") indistinguishable from a real tenant code by
// charset/length alone. Only meaningful for tunnel-derived candidates —
// never applied to production domains, where a client's real name could
// legitimately be exactly 8 characters.
function isLikelyRandomTunnelId(code: string): boolean {
  return /^[a-z0-9]{8}$/.test(code);
}

function detectTenantCode(): string {
  if (typeof window === 'undefined') return '';

  // 1. Explicit query parameter (e.g. ?tenant=klara-demo or ?t=klara-demo)
  const params = new URLSearchParams(window.location.search);
  const paramTenant = params.get('tenant') || params.get('t');
  if (paramTenant && isValidTenantCode(paramTenant)) {
    return paramTenant.trim();
  }

  const hostname = window.location.hostname.toLowerCase().trim();

  // 2. Localhost subdomains (e.g. "klara-demo.localhost", "acme.localhost")
  if (hostname.endsWith('.localhost')) {
    const sub = hostname.slice(0, -'.localhost'.length);
    if (isValidTenantCode(sub)) {
      return sub;
    }
    return '';
  }

  // 3. Dev Tunnels & Tunneling Services (e.g. "klara-demo-5173.asse.devtunnels.ms")
  const isTunnel = TUNNEL_DOMAINS.some((domain) => hostname.includes(domain));
  if (isTunnel) {
    if (hostname.includes('.devtunnels.ms')) {
      const tunnelPrefix = hostname.split('.')[0]; // e.g. "klara-demo-5173" or "25830gmh-4173"
      const portMatch = tunnelPrefix.match(/^(.+)-(?:[0-9]{4,5})$/);
      if (portMatch && portMatch[1]) {
        const candidate = portMatch[1];
        if (isValidTenantCode(candidate) && !isLikelyRandomTunnelId(candidate)) {
          return candidate;
        }
      }
    }
    // Generic dev tunnels should NEVER fall through to production domain parsing.
    // Check if there is a cached valid tenant from prior login:
    try {
      const cached = localStorage.getItem('klara.lastTenantCode');
      if (cached && isValidTenantCode(cached)) return cached.trim();
    } catch {
      // Storage may be unavailable in privacy-restricted browser contexts.
    }
    return '';
  }

  // 4. Multi-level Production Domains (e.g. "klara-demo.klara.kanerika.com" or
  // "acme.klara.ai"). Klara's per-client Kubernetes deployment gives each
  // client three subdomains — "<client>", "<client>-app", "<client>-admin" —
  // so strip a known suffix before treating the label as the tenant code,
  // or e.g. "kanerika-app" gets detected as a (nonexistent) tenant in its
  // own right instead of resolving to the real tenant "kanerika".
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    let firstPart = parts[0];
    for (const suffix of CLIENT_HOST_SUFFIXES) {
      if (firstPart.length > suffix.length && firstPart.endsWith(suffix)) {
        firstPart = firstPart.slice(0, -suffix.length);
        break;
      }
    }
    if (isValidTenantCode(firstPart)) {
      return firstPart;
    }
  }

  // 5. Fallback to cached tenant from previous login on this browser
  try {
    const cached = localStorage.getItem('klara.lastTenantCode');
    if (cached && isValidTenantCode(cached)) return cached.trim();
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }

  return '';
}

function LoginPage({ onAuth, initialError = '' }: LoginPageProps) {
  const initialMode = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('loginAs') : null;
  const detectedTenant = React.useMemo(() => detectTenantCode(), []);
  const [mode, setMode] = React.useState<'tenant' | 'superadmin'>(initialMode === 'superadmin' ? 'superadmin' : 'tenant');
  const [tenantCode, setTenantCode] = React.useState(detectedTenant || '');
  const [showManualTenant, setShowManualTenant] = React.useState(!detectedTenant);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(initialError);
  const [submitting, setSubmitting] = React.useState(false);
  const [methods, setMethods] = React.useState<any>(null);

  React.useEffect(() => {
    const activeTenant = (mode === 'superadmin' ? (tenantCode.trim() || 'system') : tenantCode.trim());
    if (!activeTenant || !isValidTenantCode(activeTenant)) {
      setMethods(null);
      return undefined;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const data = await authApi.getMethods(activeTenant);
        if (!cancelled) {
          setMethods(data);
        }
      } catch {
        if (!cancelled) {
          setMethods(null);
        }
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [tenantCode, mode]);

  const ssoAvailable =
    (methods?.sso_enabled === true || methods?.ssoEnabled === true) &&
    methods?.authentication_mode !== 'LOCAL_ONLY' &&
    methods?.authenticationMode !== 'LOCAL_ONLY';

  React.useEffect(() => {
    if (initialError) {
      setError(initialError);
    }
  }, [initialError]);

  const handleTenantSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const activeTenant = tenantCode.trim();
      if (activeTenant && isValidTenantCode(activeTenant)) {
        try {
          localStorage.setItem('klara.lastTenantCode', activeTenant);
        } catch {
          // Storage may be unavailable in privacy-restricted browser contexts.
        }
      }
      await onAuth({
        tenantCode: activeTenant,
        email: email.trim(),
        password,
      });
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuperadminSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await authApi.login({
        username: email.trim(),
        password,
        tenant_code: tenantCode.trim(),
      });
      if (!res || !res.access_token) {
        throw new Error('Invalid response from login API.');
      }
      const adminUrl = getAdminConsoleUrl();
      const destination = new URL(adminUrl, window.location.origin);
      destination.searchParams.set('access_token', res.access_token);
      if (res.refresh_token) {
        destination.searchParams.set('refresh_token', res.refresh_token);
      }
      window.location.assign(destination.toString());
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTenantSso = () => {
    if (!tenantCode.trim()) {
      setError('Enter your tenant code first.');
      return;
    }
    const adminUrl = getAdminConsoleUrl();
    window.location.assign(`${adminUrl}/auth/sso/${encodeURIComponent(tenantCode.trim())}/login?returnTo=${encodeURIComponent(window.location.origin)}`);
  };

  const handleSuperadminSso = () => {
    if (!tenantCode.trim()) {
      setError('Enter your tenant code first.');
      return;
    }
    const adminUrl = getAdminConsoleUrl();
    window.location.assign(`${adminUrl}/auth/sso/${encodeURIComponent(tenantCode.trim())}/login?returnTo=${encodeURIComponent(adminUrl)}`);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <div
      style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'minmax(320px, 1fr) minmax(420px, 1.1fr)',
        background: 'var(--bg)',
      }}
    >
      <section
        style={{
          background: 'var(--ink)',
          color: 'white',
          padding: '48px 56px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 2 }}>
          <img
            src="/klara-logo.png"
            alt="Klara"
            style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'contain', background: 'white', padding: 2 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Klara</div>
            <div style={{ fontSize: 11.5, opacity: 0.6 }}>GRSS · AI QA/QC</div>
          </div>
        </div>

        <div style={{ margin: 'auto 0', position: 'relative', zIndex: 2, maxWidth: 460 }}>
          <h1 style={{ fontSize: 38, lineHeight: 1.1, fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>
            AI-driven QA/QC for legal document processing.
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.7)', marginTop: 20 }}>
            Standardize document intake, processing, and validation with role-aware workflows and an auditable review trail.
          </p>
        </div>

        <div style={{ position: 'relative', zIndex: 2, fontSize: 11.5, color: 'rgba(255,255,255,0.4)' }}>
          © 2026 Kanerika · GRSS
        </div>
      </section>

      <main style={{ display: 'grid', placeItems: 'center', padding: '32px 56px' }}>
        <div style={{ width: 420, maxWidth: '100%' }}>
          {/* Segmented App / Role Selector Toggle */}
          <div
            style={{
              display: 'flex',
              background: '#F5F5F4',
              border: '1px solid #E7E5E4',
              borderRadius: 8,
              padding: 3,
              marginBottom: 28,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setMode('tenant');
                setError('');
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 13,
                fontWeight: mode === 'tenant' ? 600 : 500,
                color: mode === 'tenant' ? '#0A0A0A' : '#78716C',
                background: mode === 'tenant' ? '#FFFFFF' : 'transparent',
                border: 0,
                borderRadius: 6,
                boxShadow: mode === 'tenant' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Portal
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('superadmin');
                setError('');
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 13,
                fontWeight: mode === 'superadmin' ? 600 : 500,
                color: mode === 'superadmin' ? '#0A0A0A' : '#78716C',
                background: mode === 'superadmin' ? '#FFFFFF' : 'transparent',
                border: 0,
                borderRadius: 6,
                boxShadow: mode === 'superadmin' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Admin
            </button>
          </div>

          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>
              {mode === 'tenant' ? 'Sign In' : 'Admin Sign In'}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 7 }}>
              {mode === 'tenant'
                ? 'Enter your tenant credentials to continue to the Klara web app.'
                : 'Sign in to manage tenants, users, roles, SSO, and AI configuration.'}
            </p>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 18,
                padding: '11px 13px',
                borderRadius: 8,
                color: '#991b1b',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {mode === 'tenant' ? (
            <>
              <form onSubmit={handleTenantSubmit}>
                {!showManualTenant && tenantCode ? (
                  <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                      Organization: <strong style={{ color: 'var(--ink)', letterSpacing: '-0.01em' }}>{tenantCode}</strong>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowManualTenant(true)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', padding: 0 }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'block', marginBottom: 16 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Tenant code</span>
                    <input
                      value={tenantCode}
                      onChange={(event) => setTenantCode(event.target.value)}
                      autoComplete="organization"
                      placeholder="e.g. klara-demo"
                      className="auth-input"
                      required
                      autoFocus={showManualTenant}
                    />
                  </label>
                )}

                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Work email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="username"
                    className="auth-input"
                    required
                  />
                </label>

                <label style={{ display: 'block', marginBottom: 20 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    className="auth-input"
                    required
                  />
                </label>

                <button
                  type="submit"
                  className="btn primary"
                  disabled={submitting}
                  style={{ width: '100%', justifyContent: 'center', padding: '10px 14px', fontSize: 13.5 }}
                >
                  {submitting ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              {ssoAvailable && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0', color: 'var(--muted-3)' }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    <span style={{ padding: '0 12px', fontSize: 12 }}>or</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  </div>

                  <button
                    type="button"
                    onClick={handleTenantSso}
                    className="btn"
                    style={{ width: '100%', justifyContent: 'center', padding: '10px 14px', fontSize: 13.5 }}
                  >
                    Sign in with SSO
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <form onSubmit={handleSuperadminSubmit}>
                {!showManualTenant && tenantCode ? (
                  <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                      Organization: <strong style={{ color: 'var(--ink)', letterSpacing: '-0.01em' }}>{tenantCode}</strong>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowManualTenant(true)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', padding: 0 }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'block', marginBottom: 16 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Tenant code</span>
                    <input
                      value={tenantCode}
                      onChange={(event) => setTenantCode(event.target.value)}
                      autoComplete="organization"
                      placeholder="e.g. system or klara-demo"
                      className="auth-input"
                      required
                      autoFocus={showManualTenant}
                    />
                  </label>
                )}

                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Work email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="username"
                    className="auth-input"
                    required
                  />
                </label>

                <label style={{ display: 'block', marginBottom: 20 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    className="auth-input"
                    required
                  />
                </label>

                <button
                  type="submit"
                  className="btn primary"
                  disabled={submitting}
                  style={{ width: '100%', justifyContent: 'center', padding: '10px 14px', fontSize: 13.5 }}
                >
                  {submitting ? 'Signing in…' : 'Sign in as admin'}
                </button>
              </form>

              {ssoAvailable && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0', color: 'var(--muted-3)' }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    <span style={{ padding: '0 12px', fontSize: 12 }}>or</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  </div>

                  <button
                    type="button"
                    onClick={handleSuperadminSso}
                    className="btn"
                    style={{ width: '100%', justifyContent: 'center', padding: '10px 14px', fontSize: 13.5 }}
                  >
                    Sign in with SSO
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
    <Footer />
    </div>
  );
}

export default LoginPage;
